import { useSendNotification } from "@app/hooks/useNotification";
import { clientFetch } from "@app/lib/egress/client";
import {
  getErrorFromResponse,
  useFetcher,
  useSWRWithDefaults,
} from "@app/lib/swr/swr";
import type {
  GetInstinctResponseBody,
  InstinctConnectionStateType,
  PatchInstinctSettingsRequestBody,
} from "@app/types/api/me/instinct";
import { normalizeError } from "@app/types/shared/utils/error_utils";
import type { LightWorkspaceType } from "@app/types/user";
import { useCallback } from "react";
import type { Fetcher, SWRConfiguration } from "swr";

function buildInstinctUrl(workspaceId: string): string {
  return `/api/w/${workspaceId}/me/instinct`;
}

export function useInstinctConnection({
  owner,
  disabled,
  refreshInterval,
}: {
  owner: LightWorkspaceType;
  disabled?: boolean;
  refreshInterval?: SWRConfiguration<GetInstinctResponseBody>["refreshInterval"];
}) {
  const { fetcher } = useFetcher();
  const instinctFetcher: Fetcher<GetInstinctResponseBody> = fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    buildInstinctUrl(owner.sId),
    instinctFetcher,
    { disabled, refreshInterval }
  );

  return {
    connection: data ? data.connection : undefined,
    isInstinctLoading: !disabled && !error && !data,
    isInstinctError: !!error,
    mutateInstinct: mutate,
  };
}

type InstinctAction =
  | { kind: "connect" }
  | { kind: "confirm" }
  | { kind: "new_address" }
  | { kind: "disconnect" }
  | { kind: "settings"; settings: PatchInstinctSettingsRequestBody };

function requestFor(
  workspaceId: string,
  action: InstinctAction
): { url: string; init: RequestInit } {
  const url = buildInstinctUrl(workspaceId);
  switch (action.kind) {
    case "connect":
      return { url, init: { method: "POST" } };
    case "confirm":
      return { url: `${url}/confirm`, init: { method: "POST" } };
    case "new_address":
      return { url: `${url}/new_address`, init: { method: "POST" } };
    case "disconnect":
      return { url, init: { method: "DELETE" } };
    case "settings":
      return {
        url,
        init: {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(action.settings),
        },
      };
  }
}

const failureTitles: Record<InstinctAction["kind"], string> = {
  connect: "Could not start connecting Instinct",
  confirm: "Could not finish connecting Instinct",
  new_address: "Could not issue a new address",
  disconnect: "Could not disconnect Instinct",
  settings: "Could not save your Instinct settings",
};

export function useInstinctAction({ owner }: { owner: LightWorkspaceType }) {
  const sendNotification = useSendNotification();
  const { mutateInstinct } = useInstinctConnection({ owner, disabled: true });

  return useCallback(
    async (
      action: InstinctAction
    ): Promise<InstinctConnectionStateType | null | undefined> => {
      const { url, init } = requestFor(owner.sId, action);
      try {
        const res = await clientFetch(url, init);
        if (!res.ok) {
          const errorData = await getErrorFromResponse(res);
          sendNotification({
            type: "error",
            title: failureTitles[action.kind],
            description: errorData.message,
          });
          return undefined;
        }
        const body: GetInstinctResponseBody =
          res.status === 204 ? { connection: null } : await res.json();
        await mutateInstinct(body, { revalidate: false });
        return body.connection;
      } catch (e) {
        sendNotification({
          type: "error",
          title: failureTitles[action.kind],
          description: normalizeError(e).message,
        });
        return undefined;
      }
    },
    [mutateInstinct, owner.sId, sendNotification]
  );
}
